// @flow
import type {Element} from 'react'
import {pipe, concat, curry, uniqBy, prop, map, reject, isNil, sortBy} from 'ramda'
import React, {useCallback, useEffect, useState} from 'react'
import RailsSelect from 'components/ui/forms/RailsSelect/StaticRailsSelect'
import RoleCache from 'utils/OrgChartDataUtils/RoleCache'

type ImageData = {
  fa_icon?: string,
  css_classes?: string,
  image?: string,
}

type Option = {
  value: string,
  label: string,
  subtitle?: string,
  imageData?: ImageData,
};

type PlaceholderData = {
  label: string,
  imageData?: ImageData,
}

const ROLE_SELECT_WRAPPER_CLASS = 'js-slick-role-select'

export type Props = $ReadOnly<{
  defaultValue?: Object,
  id: string,
  isClearable?: boolean,
  name: string,
  pageContext: string,
  parentRoleId?: string,
  personRoleIds?: Array<string>,
  organizationId: string,
  showCircleName: boolean,
  includeIndividualInitiative?: boolean,
  individualInitiativeLabel?: string,
  options: Array<Option>,
  placeholderData?: PlaceholderData,
}>;

const roleOption = curry((includeIndividualInitiative, role) => ({
  value: includeIndividualInitiative ? `${role.id},${role.parent?.id}` : role.id,
  label: role.name,
  subtitle: role.parent ? I18n.t('shared.role_in_circle_simple', {circle_name: role.parent.name}) : '',
}))

const individualInitiativeOption = (parentRole, iiLabel) => ({
  value: `-1,${parentRole.id}`,
  label: iiLabel || I18n.t('activerecord.attributes.circle.v5.individual_action'),
  subtitle: I18n.t('shared.role_in_circle_simple', {circle_name: parentRole.name}),
})

function RailsRoleSelect({
  defaultValue, id, organizationId,
  isClearable, name, options, placeholderData, parentRoleId,
  personRoleIds, pageContext,
  showCircleName, includeIndividualInitiative, individualInitiativeLabel,
}: Props): Element<typeof RailsSelect> {
  const [optionsState, setOptionsState] = useState(options)

  const onInit = useCallback((selectWrapper) => {
    const roleInitEvent = new CustomEvent('role-init', {
      bubbles: true,
      cancelable: true,
      detail: {
        roleIdParentRoleId: defaultValue?.value,
        organizationId,
      },
    })
    selectWrapper.dispatchEvent(roleInitEvent)
  }, [defaultValue, organizationId])

  const onChange = useCallback((newOptions, currentWrapper) => {
    if (!newOptions)
      return
    // RailsSelect requires a change callback that handles multiselect.
    // RolsRoleSelect doesn't support multi, so it will always be a single option.
    // We do the array handling to make flow happy.
    const newOption = Array.isArray(newOptions) ? newOptions[0] : newOptions
    const value = newOption.value
    const roleIdParentRoleId = value.match(',') ? value : `${value},`

    const roleChangedEvent = new CustomEvent('role-changed', {
      bubbles: true,
      cancelable: true,
      detail: {
        roleIdParentRoleId,
        organizationId,
      },
    })
    currentWrapper.dispatchEvent(roleChangedEvent)
  }, [organizationId])

  useEffect(() => {
    // Get roleCache instance inside the effect to avoid hook rule violations
    const roleCache = RoleCache.instance(organizationId)
    let roles = []

    switch (pageContext) {
      case 'ROLES_PAGE':
        if (parentRoleId !== undefined && parentRoleId !== null)
          roles = roleCache.getSubRoles(parentRoleId)

        break
      case 'PERSON_PAGE':
        if (personRoleIds !== undefined)
          roles = roleCache.getRolesById(personRoleIds)
        break
      case 'PROJECT_PAGE':
        roles = roleCache.getAllRoles()
        break

      default:
        throw new Error(`Unknown page context: ${pageContext}`)
    }

    // make sure to include original/default options
    let roleOptions = pipe(
      map(roleOption(includeIndividualInitiative)),
      concat(options),
      sortBy(prop('label')),
    )(roles)

    if (includeIndividualInitiative) {
      const iiOptions = pipe(
        map(prop('parent')),
        uniqBy(prop('id')),
        reject(isNil),
        map((parentRole) => individualInitiativeOption(parentRole, individualInitiativeLabel)),
      )(roles)

      roleOptions = uniqBy(prop('value'), [...roleOptions, ...iiOptions])
    }

    setOptionsState(roleOptions)
  }, [pageContext, parentRoleId, personRoleIds, organizationId, options,
    includeIndividualInitiative, individualInitiativeLabel,
  ])

  return (
    <RailsSelect
      defaultValue={defaultValue}
      showSubtitle={showCircleName}
      id={id}
      isMulti={false}
      isSearchable
      isClearable={isClearable}
      name={name}
      onChange={onChange}
      onInit={onInit}
      options={optionsState}
      placeholderData={placeholderData}
      wrapperClass={ROLE_SELECT_WRAPPER_CLASS}
    />
  )
}

export default RailsRoleSelect
